import React, { createContext, useContext, useState, useMemo, ReactNode, useCallback } from 'react'

import { OfferTranslations } from './types'

interface CacheContextType {
  get: (key: number) => OfferTranslations | undefined
  set: (key: number, value: OfferTranslations) => void
  has: (key: number) => boolean
  clear: () => void
}

const CacheContext = createContext<CacheContextType | undefined>(undefined)

interface CacheProviderProps {
  children: ReactNode
}

export const CacheProvider: React.FC<CacheProviderProps> = ({ children }) => {
  const [cache, setCache] = useState<Map<number, OfferTranslations>>(new Map())

  const get = useCallback((key: number): OfferTranslations | undefined => {
    return cache.get(key)
  }, [cache])

  const set = useCallback((key: number, value: OfferTranslations) => {
    setCache(prevCache => {
      const newCache = new Map(prevCache)
      newCache.set(key, value)
      return newCache
    })
  }, [])

  const has = useCallback((key: number): boolean => {
    return cache.has(key)
  }, [cache])

  const clear = useCallback(() => {
    setCache(new Map())
  }, [])

  const contextValue = useMemo(
    () => ({
      get,
      set,
      has,
      clear,
    }),
    [get, set, has, clear]
  )

  return <CacheContext.Provider value={contextValue}>{children}</CacheContext.Provider>
}

export const useCache = () => {
  const context = useContext(CacheContext)
  if (context === undefined) {
    throw new Error('useCache must be used within a CacheProvider')
  }

  return context
}
